// connector.js

/**
 * LexeekConnector handles all server communications and storage management
 */
export class LexeekConnector {
    constructor(config) {
        this.config = {
            apiKey: config.apiKey,
            endpoint: config.endpoint || 'http://127.0.0.1:8888',
        };
        
        this.currentSources = [];
        this.origin = this.getOrigin();
        this.sessionId = null;
        this.messages = [];
        this.styles = null;
    }

    getOrigin() {
        try {
            return typeof window !== 'undefined' && window.location 
                ? window.location.origin 
                : 'default-origin';
        } catch (error) {
            console.warn('Unable to determine origin:', error);
            return 'default-origin';
        }
    }

    getStoredSession() {
        return this.sessionId;
    }

    setStoredSession(sessionId) {
        this.sessionId = sessionId;
    }

    clearStoredSession() {
        this.sessionId = null;
    }

    getStoredStyles() {
        return this.styles;
    }

    setStoredStyles(styles) {
        this.styles = styles;
    }

    getStoredMessages() {
        return this.messages;
    }

    setStoredMessages(messages) {
        this.messages = messages;
    }

    clearStoredMessages() {
        this.messages = [];
    }

    async getAuthHeaders() {
        const headers = {
            'X-LEXEEK-API-KEY': this.config.apiKey,
            'Content-Type': 'application/json'
        };
        
        const sessionId = this.getStoredSession();
        if (sessionId) {
            headers['X-LEXEEK-SESSION-ID'] = sessionId;
        }

        return headers;
    }

    async makeRequest(url, options = {}) {
        const headers = await this.getAuthHeaders();
        
        try {
            const response = await fetch(url, {
                ...options,
                headers: {
                    ...headers,
                    ...options.headers
                }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Invalid API key');
                }
                throw new Error(`Request failed with status ${response.status}`);
            }

            const newSessionId = response.headers.get('X-LEXEEK-SESSION-ID');
            if (newSessionId) {
                this.setStoredSession(newSessionId);
            }

            return response;
        } catch (error) {
            console.error('Request error:', error);
            throw error;
        }
    }

    async initialize() {
        try {
            const response = await fetch(`${this.config.endpoint}/xeek-kit/style`, {
                headers: {
                    'X-LEXEEK-API-KEY': this.config.apiKey
                }
            });

            if (!response.ok) {
                throw new Error('Failed to initialize');
            }

            const css = await response.text();
            const newSessionId = response.headers.get('X-LEXEEK-SESSION-ID');

            if (newSessionId) {
                this.setStoredSession(newSessionId);
                this.setStoredStyles(css);
            }

            return {
                css,
                sessionId: newSessionId,
                messages: this.getStoredMessages()
            };
        } catch (error) {
            console.error('Initialization error:', error);
            throw error;
        }
    }

    async validateSession() {
        const sessionId = this.getStoredSession();
        if (!sessionId) return false;
        
        try {
            const response = await fetch(`${this.config.endpoint}/search/instant`, {
                method: 'POST',
                headers: await this.getAuthHeaders(),
                body: JSON.stringify({ query: '' })
            });
            
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    async search(query) {
        return this.makeRequest(`${this.config.endpoint}/search/instant`, {
            method: 'POST',
            body: JSON.stringify({ query })
        });
    }

    async startConversation(message) {
        if (!message?.trim()) {
            throw new Error('Empty message');
        }
    
        console.log('Sending payload:', { query: message });
    
        const response = await this.makeRequest(
            `${this.config.endpoint}/search/conversation`,
            {
                method: 'POST',
                body: JSON.stringify({ query: message })
            }
        );
    
        return response;
    }

    handleStreamResponse(response, onChunk, onError, onComplete) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
            try {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        if (buffer.trim()) {
                            const lines = buffer.split('\n');
                            for (const line of lines) {
                                this.processStreamChunk(line, onChunk);
                            }
                        }
                        onComplete && onComplete();
                        break;
                    }

                    const text = decoder.decode(value, { stream: true });
                    buffer += text;

                    const parts = buffer.split('\n\n');
                    buffer = parts.pop() || '';

                    for (const part of parts) {
                        this.processStreamChunk(part, onChunk);
                    }
                }
            } catch (error) {
                console.error('Stream processing error:', error);
                onError && onError(error);
            }
        };

        processStream();
    }

    processStreamChunk(chunk, onChunk) {
        const trimmed = chunk.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) return;

        try {
            const data = JSON.parse(trimmed.slice(6));
            onChunk(data);

            if (data.sources && !data.answer) {
                this.currentSources = data.sources;
            } else if (data.answer_delta !== undefined) {
                if (data.is_answer_stream_finished) {
                    const messages = this.getStoredMessages();
                    messages.push({ role: 'assistant', content: data.answer });
                    this.setStoredMessages(messages);
                }
            }
        } catch (error) {
            console.error('Error parsing stream chunk:', error);
        }
    }

    async clearChat() {
        const response = await this.makeRequest(
            `${this.config.endpoint}/search/clear_conversation`,
            { method: 'POST' }
        );
        this.clearStoredMessages();
        return response;
    }

    cleanup() {
        this.clearStoredSession();
        this.clearStoredMessages();
    }
}

export default LexeekConnector;